import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { matchService } from '../../../services/matchService';
import { tournamentService } from '../../../services/tournamentService';
import { buildMatchReportFromEvents } from '../../../utils/matchReportStats';

const PREVIEW_FALLBACK = '/assets/images/no_image.png';

const MatchReportModal = ({ result, competitionType = 'league', onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report, setReport] = useState(null);
  const [activeTab, setActiveTab] = useState('team');

  useEffect(() => {
    if (!result?.id) return undefined;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        let matchDetail = null;
        let events = [];

        if (competitionType === 'tournament' || result?.sourceType === 'tournament') {
          matchDetail = await tournamentService.getGameById(result.id);
        } else {
          matchDetail = await matchService.getById(result.id);
          events = matchDetail?.events || [];
        }

        const homeTeam = {
          id: matchDetail?.homeTeamId || matchDetail?.homeTeam?.id,
          name:
            matchDetail?.homeTeam?.name ||
            matchDetail?.homeTeam?.teamName ||
            result?.homeTeam?.name ||
            'Home',
          shortName:
            matchDetail?.homeTeam?.shortName ||
            result?.homeTeam?.shortName ||
            (matchDetail?.homeTeam?.teamName || result?.homeTeam?.name || 'HOM')
              .substring(0, 3)
              .toUpperCase(),
          logo:
            matchDetail?.homeTeam?.logoUrl ||
            result?.homeTeam?.logo ||
            PREVIEW_FALLBACK,
        };

        const awayTeam = {
          id: matchDetail?.awayTeamId || matchDetail?.awayTeam?.id,
          name:
            matchDetail?.awayTeam?.name ||
            matchDetail?.awayTeam?.teamName ||
            result?.awayTeam?.name ||
            'Away',
          shortName:
            matchDetail?.awayTeam?.shortName ||
            result?.awayTeam?.shortName ||
            (matchDetail?.awayTeam?.teamName || result?.awayTeam?.name || 'AWY')
              .substring(0, 3)
              .toUpperCase(),
          logo:
            matchDetail?.awayTeam?.logoUrl ||
            result?.awayTeam?.logo ||
            PREVIEW_FALLBACK,
        };

        const homeScore = matchDetail?.homeScore ?? result?.homeScore ?? 0;
        const awayScore = matchDetail?.awayScore ?? result?.awayScore ?? 0;

        const stats = buildMatchReportFromEvents({
          events,
          homeTeamId: homeTeam.id,
          awayTeamId: awayTeam.id,
          homeScore,
          awayScore,
        });

        if (cancelled) return;

        setReport({
          homeTeam,
          awayTeam,
          homeScore,
          awayScore,
          date: matchDetail?.scheduledDate || result?.date,
          venue: matchDetail?.venue || result?.venue || 'TBD',
          overtime: result?.overtime,
          stageName: matchDetail?.stageName || result?.stageName || null,
          matchNotes: matchDetail?.matchNotes || null,
          ...stats,
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load match report');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [result, competitionType]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const calculatePercentage = (made, attempted) => {
    if (!attempted) return '0.0';
    return ((made / attempted) * 100).toFixed(1);
  };

  const TeamStatRow = ({ label, homeValue, awayValue, isPercentage = false }) => (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-b-0">
      <div className="text-sm text-muted-foreground w-1/3">{label}</div>
      <div className="text-sm font-medium text-foreground w-1/3 text-center">
        {isPercentage ? `${homeValue}%` : homeValue}
      </div>
      <div className="text-sm font-medium text-foreground w-1/3 text-center">
        {isPercentage ? `${awayValue}%` : awayValue}
      </div>
    </div>
  );

  const PlayerTable = ({ players, emptyLabel }) => {
    if (!players?.length) {
      return <p className="text-sm text-muted-foreground py-4 text-center">{emptyLabel}</p>;
    }
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="text-left py-2 pr-2 font-medium">#</th>
              <th className="text-left py-2 pr-2 font-medium">Player</th>
              <th className="text-center py-2 px-1 font-medium">PTS</th>
              <th className="text-center py-2 px-1 font-medium">REB</th>
              <th className="text-center py-2 px-1 font-medium">AST</th>
              <th className="text-center py-2 px-1 font-medium">STL</th>
              <th className="text-center py-2 px-1 font-medium">BLK</th>
              <th className="text-center py-2 px-1 font-medium">FG</th>
              <th className="text-center py-2 px-1 font-medium">3PT</th>
              <th className="text-center py-2 px-1 font-medium">FT</th>
              <th className="text-center py-2 pl-1 font-medium">PF</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id} className="border-b border-border/60">
                <td className="py-2 pr-2 text-muted-foreground">{p.jerseyNumber}</td>
                <td className="py-2 pr-2 font-medium text-foreground">{p.name}</td>
                <td className="py-2 px-1 text-center font-semibold">{p.points}</td>
                <td className="py-2 px-1 text-center">{p.rebounds}</td>
                <td className="py-2 px-1 text-center">{p.assists}</td>
                <td className="py-2 px-1 text-center">{p.steals}</td>
                <td className="py-2 px-1 text-center">{p.blocks}</td>
                <td className="py-2 px-1 text-center">
                  {p.fieldGoalsMade}/{p.fieldGoalsAttempted}
                </td>
                <td className="py-2 px-1 text-center">
                  {p.threePointersMade}/{p.threePointersAttempted}
                </td>
                <td className="py-2 px-1 text-center">
                  {p.freeThrowsMade}/{p.freeThrowsAttempted}
                </td>
                <td className="py-2 pl-1 text-center">{p.fouls}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-lg card-shadow w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 sm:p-6 border-b border-border flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Match Report</h3>
            <p className="text-xs text-muted-foreground mt-1">Full game statistics</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" aria-label="Close">
            <Icon name="X" size={16} />
          </Button>
        </div>

        <div className="overflow-y-auto flex-1 p-4 sm:p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-16">
              <Icon name="Loader2" size={28} className="text-primary animate-spin" />
            </div>
          )}

          {error && !isLoading && (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={36} className="text-error mx-auto mb-3" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {report && !isLoading && !error && (
            <div className="space-y-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-muted shrink-0 border border-border">
                    <Image
                      src={report.homeTeam.logo}
                      alt={`${report.homeTeam.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-foreground truncate">{report.homeTeam.name}</p>
                    <p className="text-xs text-muted-foreground">{report.homeTeam.shortName}</p>
                  </div>
                </div>

                <div className="text-center shrink-0 px-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-foreground">{report.homeScore}</span>
                    <span className="text-muted-foreground">-</span>
                    <span className="text-2xl font-bold text-foreground">{report.awayScore}</span>
                  </div>
                  {report.overtime && (
                    <span className="text-xs text-error font-medium">OT</span>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-1 min-w-0 justify-end">
                  <div className="min-w-0 text-right">
                    <p className="font-semibold text-foreground truncate">{report.awayTeam.name}</p>
                    <p className="text-xs text-muted-foreground">{report.awayTeam.shortName}</p>
                  </div>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-muted shrink-0 border border-border">
                    <Image
                      src={report.awayTeam.logo}
                      alt={`${report.awayTeam.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1">
                  <Icon name="Calendar" size={12} />
                  {formatDate(report.date)}
                </span>
                {formatTime(report.date) && (
                  <span className="inline-flex items-center gap-1">
                    <Icon name="Clock" size={12} />
                    {formatTime(report.date)}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Icon name="MapPin" size={12} />
                  {report.venue}
                </span>
                {report.stageName && (
                  <span className="inline-flex items-center gap-1">
                    <Icon name="Layers" size={12} />
                    {report.stageName}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-1">
                {[
                  { id: 'team', label: 'Team Stats', icon: 'BarChart3' },
                  { id: 'players', label: 'Box Score', icon: 'Users' },
                  { id: 'playbyplay', label: 'Play-by-Play', icon: 'List' },
                ].map((tab) => (
                  <Button
                    key={tab.id}
                    variant={activeTab === tab.id ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab(tab.id)}
                    iconName={tab.icon}
                    iconPosition="left"
                  >
                    {tab.label}
                  </Button>
                ))}
              </div>

              {activeTab === 'team' && (
                <div className="space-y-4">
                  {!report.hasDetailedStats && (
                    <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
                      Detailed play-by-play stats were not recorded for this game. Final score is
                      shown above.
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-b-2 border-border">
                    <div className="text-sm font-semibold text-muted-foreground w-1/3">Statistic</div>
                    <div className="text-sm font-semibold text-foreground w-1/3 text-center">
                      {report.homeTeam.shortName}
                    </div>
                    <div className="text-sm font-semibold text-foreground w-1/3 text-center">
                      {report.awayTeam.shortName}
                    </div>
                  </div>
                  <TeamStatRow
                    label="Points"
                    homeValue={report.home.points}
                    awayValue={report.away.points}
                  />
                  <TeamStatRow
                    label="Field Goals"
                    homeValue={`${report.home.fieldGoalsMade}/${report.home.fieldGoalsAttempted}`}
                    awayValue={`${report.away.fieldGoalsMade}/${report.away.fieldGoalsAttempted}`}
                  />
                  <TeamStatRow
                    label="Field Goal %"
                    homeValue={calculatePercentage(
                      report.home.fieldGoalsMade,
                      report.home.fieldGoalsAttempted
                    )}
                    awayValue={calculatePercentage(
                      report.away.fieldGoalsMade,
                      report.away.fieldGoalsAttempted
                    )}
                    isPercentage
                  />
                  <TeamStatRow
                    label="3-Pointers"
                    homeValue={`${report.home.threePointersMade}/${report.home.threePointersAttempted}`}
                    awayValue={`${report.away.threePointersMade}/${report.away.threePointersAttempted}`}
                  />
                  <TeamStatRow
                    label="Free Throws"
                    homeValue={`${report.home.freeThrowsMade}/${report.home.freeThrowsAttempted}`}
                    awayValue={`${report.away.freeThrowsMade}/${report.away.freeThrowsAttempted}`}
                  />
                  <TeamStatRow
                    label="Rebounds"
                    homeValue={report.home.rebounds}
                    awayValue={report.away.rebounds}
                  />
                  <TeamStatRow
                    label="Assists"
                    homeValue={report.home.assists}
                    awayValue={report.away.assists}
                  />
                  <TeamStatRow
                    label="Steals"
                    homeValue={report.home.steals}
                    awayValue={report.away.steals}
                  />
                  <TeamStatRow
                    label="Blocks"
                    homeValue={report.home.blocks}
                    awayValue={report.away.blocks}
                  />
                  <TeamStatRow
                    label="Turnovers"
                    homeValue={report.home.turnovers}
                    awayValue={report.away.turnovers}
                  />
                  <TeamStatRow
                    label="Fouls"
                    homeValue={report.home.fouls}
                    awayValue={report.away.fouls}
                  />
                </div>
              )}

              {activeTab === 'players' && (
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">
                      {report.homeTeam.name}
                    </h4>
                    <PlayerTable
                      players={report.homePlayers}
                      emptyLabel="No player box score available for this side."
                    />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2">
                      {report.awayTeam.name}
                    </h4>
                    <PlayerTable
                      players={report.awayPlayers}
                      emptyLabel="No player box score available for this side."
                    />
                  </div>
                </div>
              )}

              {activeTab === 'playbyplay' && (
                <div className="space-y-2">
                  {report.playByPlay?.length ? (
                    report.playByPlay.map((event) => (
                      <div
                        key={event.id}
                        className="flex items-start gap-3 py-2 border-b border-border last:border-0"
                      >
                        <div className="text-xs text-muted-foreground w-14 shrink-0 pt-0.5">
                          Q{event.quarter ?? '—'}
                          {event.timeRemaining ? ` ${event.timeRemaining}` : ''}
                        </div>
                        <div className="text-sm text-foreground flex-1">
                          {event.description || event.eventType}
                        </div>
                        {event.points > 0 && (
                          <div className="text-xs font-semibold text-primary">+{event.points}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No play-by-play events were saved for this match.
                    </p>
                  )}
                </div>
              )}

              {report.matchNotes && (
                <div className="bg-muted/40 rounded-lg p-3">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Notes</p>
                  <p className="text-sm text-foreground">{report.matchNotes}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MatchReportModal;
